<template>
  <div v-if="show" class="enhancement-narrative">
    <header class="enhancement-narrative__header">
      <h3>AI Narrative</h3>
      <div v-if="modelId || latencyMs" class="meta">
        <span v-if="modelId">Model: {{ modelId }}</span>
        <span v-if="latencyMs"
          >Latency: {{ (latencyMs / 1000).toFixed(2) }}s</span
        >
        <span v-if="tokenUsagePrompt != null && tokenUsageCompletion != null">
          Tokens: {{ tokenUsagePrompt + tokenUsageCompletion }} (P{{
            tokenUsagePrompt
          }}/C{{ tokenUsageCompletion }})
        </span>
      </div>
    </header>

    <div v-if="loading" class="enhancement-narrative__loading">
      <p>Generating narrative...</p>
    </div>
    <div
      v-else-if="error"
      class="enhancement-narrative__error"
      data-cy="ai-enhancement-error"
    >
      <p>{{ error }}</p>
      <button v-if="canRetry" @click="retry">Retry</button>
    </div>
    <div
      v-else-if="narrative"
      class="enhancement-narrative__body"
      data-cy="ai-enhancement-narrative"
    >
      <div v-if="isStructured(narrative)">
        <p class="summary">{{ narrative.summary }}</p>

        <div class="narrative-grid">
          <div class="narrative-card pros-card">
            <h4>PROS</h4>
            <ul>
              <li v-for="(item, i) in narrative.pros" :key="i">{{ item }}</li>
            </ul>
          </div>
          <div class="narrative-card cons-card">
            <h4>CONS</h4>
            <ul>
              <li v-for="(item, i) in narrative.cons" :key="i">{{ item }}</li>
            </ul>
          </div>
        </div>

        <div
          v-if="narrative.watchItems?.length"
          class="narrative-section watch-items"
        >
          <h4>Watch Items</h4>
          <ul>
            <li v-for="(item, i) in narrative.watchItems" :key="i">
              {{ item }}
            </li>
          </ul>
        </div>

        <div v-if="narrative.rationale" class="narrative-section rationale">
          <h4>Rationale</h4>
          <p>{{ narrative.rationale }}</p>
        </div>
      </div>
      <pre v-else>{{ narrative }}</pre>
    </div>
    <div v-else class="enhancement-narrative__placeholder">
      <p>No narrative available.</p>
    </div>

    <section v-if="warningMeta.length" class="enhancement-narrative__warnings">
      <h4>Warnings</h4>
      <ul>
        <li
          v-for="w in warningMeta"
          :key="w.code"
          :class="['warn', 'severity-' + w.severity]"
        >
          <strong>{{ w.label }}:</strong> {{ w.description }}
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useAISuggestionsStore } from "@/store/aiSuggestions";
import { mapWarnings } from "@/libs/ai/warningLabels";
import type { AINarrativeContent } from "@/libs/api/aiEquipmentNarrative";

const props = withDefaults(
  defineProps<{
    priority?: "performance" | "runlife" | "cost";
  }>(),
  {
    priority: "performance",
  },
);

const store = useAISuggestionsStore();

const show = computed(() => {
  const hasId = store.enhancementIds?.[props.priority];
  const hasPending = store.narrativePending;
  const hasNarrative = store.narratives?.[props.priority];
  return !!(hasId || hasPending || hasNarrative);
});
const loading = computed(
  () =>
    store.enhancementLoading?.[props.priority] ||
    (store.narrativePending && !store.enhancementDone?.[props.priority]),
);
const error = computed(() => store.enhancementError?.[props.priority]);
const narrative = computed(() => store.narratives?.[props.priority]);
const latencyMs = computed(() => store.latencyMs?.[props.priority]);
const modelId = computed(() => store.modelId?.[props.priority]);
const tokenUsagePrompt = computed(
  () => store.tokenUsagePrompt?.[props.priority],
);
const tokenUsageCompletion = computed(
  () => store.tokenUsageCompletion?.[props.priority],
);
const warningMeta = computed(() => mapWarnings(store.warnings));
const canRetry = computed(
  () =>
    !!store.enhancementError?.[props.priority] &&
    !!store.enhancementIds?.[props.priority] &&
    !loading.value,
);

function isStructured(n: any): n is AINarrativeContent {
  return n && typeof n === "object" && "summary" in n;
}

function retry() {
  store.retryEnhancementPolling();
}
</script>

<style scoped>
.enhancement-narrative {
  border: 1px solid var(--border-main);
  padding: 0.75rem;
  border-radius: var(--border-radius);
  background: var(--surface-0);
  color: var(--text-color);
}
.enhancement-narrative__header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.enhancement-narrative__header h3 {
  margin: 0;
  font-size: 1rem;
  color: var(--text-color);
}
.meta {
  font-size: 0.75rem;
  color: var(--gray-600);
  display: flex;
  gap: 0.5rem;
}
.enhancement-narrative__body pre {
  white-space: pre-wrap;
  font-family: var(--monospace-family, monospace);
  background: var(--surface-100);
  color: var(--text-color);
  padding: 0.5rem;
  border-radius: var(--border-radius);
  margin: 0;
}
.summary {
  font-weight: 500;
  margin-bottom: 1rem;
  line-height: 1.5;
  color: var(--text-color);
}
.narrative-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

/* Card-style banners for Pros and Cons */
.narrative-card {
  border: 2px solid;
  border-radius: 6px;
  padding: 1rem 1.5rem;
}

.narrative-card h4 {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid;
}

.narrative-card ul {
  margin: 0;
  padding-left: 1.2rem;
}

.narrative-card li {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  line-height: 1.5;
  color: var(--text-color);
}

/* Pros card - green styling matching "Populate Design" banner */
.pros-card {
  border-color: var(--green-500);
  background-color: rgba(34, 197, 94, 0.08);
}

.pros-card h4 {
  color: var(--text-color);
  border-bottom-color: var(--green-500);
}

/* Cons card - red styling with transparent background */
.cons-card {
  border-color: var(--red-500);
  background-color: rgba(239, 68, 68, 0.08);
}

.cons-card h4 {
  color: var(--text-color);
  border-bottom-color: var(--red-500);
}

.narrative-section h4 {
  font-size: 0.85rem;
  text-transform: uppercase;
  color: var(--gray-700);
  margin: 0 0 0.5rem 0;
  border-bottom: 1px solid var(--border-main);
  padding-bottom: 0.25rem;
}
.narrative-section ul {
  margin: 0;
  padding-left: 1.2rem;
}
.narrative-section li {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}
.pros li {
  color: var(--success-700);
}
.cons li {
  color: var(--danger-700);
}
.watch-items {
  margin-bottom: 1rem;
  border: 2px solid var(--orange-500);
  background-color: rgba(249, 115, 22, 0.08);
  padding: 1rem 1.5rem;
  border-radius: 6px;
}
.watch-items h4 {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-color);
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--orange-500);
}
.watch-items ul {
  margin: 0;
  padding-left: 1.2rem;
}
.watch-items li {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  line-height: 1.5;
  color: var(--text-color);
}
.rationale {
  font-size: 0.9rem;
  color: var(--text-color);
  background: var(--surface-100);
  padding: 0.75rem;
  border-radius: var(--border-radius);
}
.enhancement-narrative__loading {
  font-style: italic;
  color: var(--gray-600);
  padding: 1rem;
  text-align: center;
}
.enhancement-narrative__error {
  color: var(--danger-700);
}
.enhancement-narrative__warnings {
  margin-top: 1rem;
  border-top: 1px solid var(--border-main);
  padding-top: 0.75rem;
}
.enhancement-narrative__warnings ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.enhancement-narrative__warnings li {
  padding: 0.25rem 0;
  font-size: 0.85rem;
}
.severity-error {
  color: var(--danger-700);
}
.severity-warn {
  color: var(--warning-700);
}
.severity-info {
  color: var(--info-700);
}

@media (max-width: 600px) {
  .narrative-grid {
    grid-template-columns: 1fr;
  }
}
</style>
